<template>
  <div class="w-full">
    <div v-if="!analysisFinished" class="text-12 text-red-500 my-8 bg-red-50 p-4 rounded-6 flex items-center">
      <img src="@/assets/img/red_exclamation_mark.svg" class="w-16 h-16 mr-8" />
      <span>Values are estimations. Please calculate for more accuracy.</span>
    </div>
    <div class="flex justify-start items-baseline pb-20">
      <Toggle :possible-classes="levels" :selected-class="selectedLevel" @selected="selectLevel($event)" />
    </div>
    <hr class="border-gray-50 mb-16" />
    <div class="flex flex-col gap-20 pb-16">
      <div class="flex flex-col gap-8">
        <div class="text-14 text-gray-900">Material production</div>
        <div class="flex items-center justify-between">
          <div class="text-13 text-gray-600">Energy consumption</div>
          <div class="flex items-center gap-4">
            <div class="text-13 text-gray-1000 font-bold">{{ $formatTwoDecimalPlaces(materialEnergyConsumption) }}</div>
            <div class="text-13 text-gray-400">kg CO2</div>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div class="text-13 text-gray-600">Consumables</div>
          <div class="flex items-center gap-4">
            <div class="text-13 text-gray-1000 font-bold">-</div>
            <div class="text-13 text-gray-400">kg CO2</div>
          </div>
        </div>
      </div>
      <hr class="border-gray-50 mb-8" />
      <div class="flex flex-col gap-8">
        <div class="text-14 text-gray-900">Part production</div>
        <div class="flex items-center justify-between">
          <div class="text-13 text-gray-600">Energy consumption</div>
          <div class="flex items-center gap-4">
            <div class="text-13 text-gray-1000 font-bold">{{ $formatTwoDecimalPlaces(partEnergyConsumption) }}</div>
            <div class="text-13 text-gray-400">kg CO2</div>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div class="text-13 text-gray-600">Consumables</div>
          <div class="flex items-center gap-4">
            <div class="text-13 text-gray-1000 font-bold">-</div>
            <div class="text-13 text-gray-400">kg CO2</div>
          </div>
        </div>
      </div>
      <hr class="border-gray-50 mb-8" />
      <div class="flex items-center justify-end gap-4">
        <div class="text-14 text-gray-900 font-bold">Total</div>
        <div class="flex items-center gap-8">
          <div class="text-14 text-gray-1000 font-bold">{{ $formatTwoDecimalPlaces(totalCo2) }}</div>
          <div class="text-14 text-gray-400">kg Co2</div>
        </div>
      </div>
    </div>
    <CarbonChartPart
      v-if="segments && segments.length && $formatTwoDecimalPlaces(totalCo2) !== '0,00'"
      :segments="segments"
      :total-segment-index="totalSegmentIndex"
      :total-value="$formatTwoDecimalPlaces(totalCo2)"
      :total-unit="'kg Co2-eq'"
    />
    <hr class="border-gray-50 mb-8" />
    <ProcessChain
      :chain="activeProcessChain"
      editable
      :selected-level="selectedLevel"
      property="co2"
      :materialEnergyConsumption="materialEnergyConsumption"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import ProcessChain from '@/components/PartWorkflow/ProcessChain/ProcessChain.vue';
import CarbonChartPart from '@/components/PartWorkflow/Comparison/tablecells/carbon/components/CarbonChartPart.vue';
import Toggle from '@/components/Reusable/Toggle.vue';
import { analysisFinished } from '@/composables/processChainHandling.js';

export default {
  name: 'CarbonFootprint',

  components: {
    ProcessChain,
    Toggle,
    CarbonChartPart,
  },

  data() {
    return {
      selectedLevel: {
        _pp: 'Part',
      },
      totalSegmentIndex: 0,
    };
  },

  computed: {
    ...mapState(['investigationDetails', 'part']),

    analysisFinished() {
      return analysisFinished(this.activeInvestigationDetails.uid);
    },

    activeInvestigationDetails() {
      return this.investigationDetails;
    },

    activeProcessChain() {
      let allChains = this.part.process_chains;
      return allChains[this.activeInvestigationDetails.uid];
    },
    materialEnergyConsumption() {
      const level = Object.keys(this.selectedLevel)[0];
      return this.activeProcessChain.co2['co2_ec' + level];
    },
    partEnergyConsumption() {
      const level = Object.keys(this.selectedLevel)[0];
      return this.activeProcessChain.co2['co2' + level] - this.activeProcessChain.co2['co2_ec' + level];
    },
    partSegments() {
      // Create segments from process steps with energy consumption values
      const segments = [];
      const level = Object.keys(this.selectedLevel)[0];
      if (this.activeProcessChain && this.activeProcessChain.process_steps) {
        const orangeColors = ['#FDF3E7', '#FCE3C4', '#F9C17F', '#F6A03A', '#D16A00']; // orange-50, orange-100, orange-200, orange-300, orange-500
        Object.keys(this.activeProcessChain.process_steps).forEach((step, index) => {
          let value = Number(
            Number(
              this.activeProcessChain.process_steps[step].co2['co2' + level] -
                this.activeProcessChain.process_steps[step].co2['co2_ec' + level]
            ).toFixed(2)
          );
          if (value > 0) {
            segments.push({
              label: this.activeProcessChain.process_steps[step].name || 'Process Step',
              value: value,
              color: orangeColors[index % orangeColors.length], // Cycle through different orange hues
            });
          }
        });
      }
      // Go through all process steps
      // for (const step of this.activeProcessChain.process_steps) {
      //   // Check if the step has energy consumption data for the selected level
      // if (step.co2 && step.co2['co2' + level] !== undefined && step.co2['co2' + level] > 0) {
      // console.log(step);
      //     segments.push({
      //       label: step.name || 'Process Step',
      //       value: Number(Number(step.co2['co2' + level]).toFixed(2)),
      //       color: '#F9C17F', // Using orange-200 from the tailwind config
      //       tooltip: {
      //         lines: [
      //           { label: 'Process', value: step.name || 'Unknown' },
      //           { label: 'CO₂ Footprint', value: `${Number(step.co2['co2' + level]).toFixed(2)} kg` },
      //         ],
      //       },
      //     });
      //   }
      // }
      // }
      // }
      return segments;
    },

    formattedMaterialEnergyConsumption() {
      return Number(Number(this.materialEnergyConsumption).toFixed(2));
    },

    materialSegment() {
      return {
        label: 'Material',
        value: this.formattedMaterialEnergyConsumption,
        color: '#B8CE45',
      };
    },

    segments() {
      const segments = [...this.partSegments];
      if (this.formattedMaterialEnergyConsumption > 0) {
        segments.unshift(this.materialSegment);
      }
      return segments;
    },

    totalCo2() {
      return this.materialEnergyConsumption + this.partEnergyConsumption;
    },

    levels() {
      let labels = {
        _pp: 'Part',
        _pj: '',
        _pl: 'Lot [' + this.activeProcessChain.lot_size + ' parts]',
      };

      if (this.activeProcessChain.lot_only) {
        labels['_pj'] = 'Job [Ø ' + this.activeProcessChain.parts_pj + ' parts]';
      } else {
        labels['_pj'] = 'Job [' + this.activeProcessChain.parts_pj + ' parts]';
      }
      return labels;
    },
  },

  methods: {
    selectLevel(event) {
      this.selectedLevel = event;
    },
  },
};
</script>
